import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditByIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit data to match the GraphQL query structure
 */
const mapTrpcInternalAuditToGraphQL = (
  trpcData: InternalAuditByIdResponse
): GetInternalAuditByIdQuery => {
  return {
    internal_audit_entity: trpcData.internal_audit_entity,
  };
};

export const useGetInternalAuditById = (
  internalAuditId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditEntity.internalAuditById.queryOptions({
      internalAuditId,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcInternalAuditToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
